/* =============================================================================
 * posthog-engagement.js
 *
 * Engagement quality signals — the data foundation for Quality_PDP_View
 * (Meta CAPI custom optimization event, scoped 4-8 weeks from now).
 *
 * Loaded once in theme.liquid <head>, after posthog-utils.js:
 *   <script src="{{ 'posthog-engagement.js' | asset_url }}" defer></script>
 *
 * Events fired:
 *   - Engaged Time Heartbeat   (every 15s while page is visible)
 *   - Scroll Milestone         (first crossing of 25/50/75/100% scroll depth)
 *   - Engaged Pageview         (synthetic, on leave — carries final dwell + scroll)
 *
 * Volume sanity:
 *   At 152 sessions/day × ~4 heartbeats/min × ~2 min avg = ~1,200 heartbeats/day
 *   Well under PostHog 1M/month free tier.
 *
 * Quality_PDP_View definition (provisional — to validate after 4 weeks of data):
 *   dwell_seconds >= 30 AND max_scroll_pct >= 50 AND pages_viewed > 1 (non-bounce)
 * ============================================================================= */

(function () {
  'use strict';

  var HEARTBEAT_INTERVAL_MS = 15000;
  var SCROLL_MILESTONES = [25, 50, 75, 100];

  // Per-pageview state
  var pageStartMs = performance.now();
  var activeMs = 0;
  var lastTickMs = pageStartMs;
  var isPageActive = !document.hidden;
  var heartbeatTimer = null;
  var milestonesFired = {};
  var maxScrollPct = 0;
  var engagedPageviewFired = false;
  var pathnameAtLoad = window.location.pathname;

  // ---------------------------------------------------------------------------
  // Active-time tracking
  // Pauses on visibility-hidden; resumes on visible. Avoids counting background tabs.
  // ---------------------------------------------------------------------------
  function accrueActiveTime() {
    if (isPageActive) {
      var now = performance.now();
      activeMs += now - lastTickMs;
      lastTickMs = now;
    }
  }

  function getCurrentDwellSeconds() {
    accrueActiveTime();
    return Math.round(activeMs / 1000);
  }

  // ---------------------------------------------------------------------------
  // Heartbeat — fires every 15s while page is visible
  // ---------------------------------------------------------------------------
  function fireHeartbeat() {
    try {
      if (!isPageActive) return;
      accrueActiveTime();
      window.safeCapture('Engaged Time Heartbeat', {
        cumulative_active_seconds: Math.round(activeMs / 1000),
        pathname: pathnameAtLoad,
        max_scroll_pct: maxScrollPct
      });
    } catch (e) {}
  }

  function startHeartbeat() {
    if (heartbeatTimer) return;
    heartbeatTimer = setInterval(fireHeartbeat, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Visibility tracking — pause active timer when hidden
  // ---------------------------------------------------------------------------
  function onVisibilityChange() {
    try {
      if (document.hidden) {
        accrueActiveTime();
        isPageActive = false;
        stopHeartbeat();
        // Fire synthetic Engaged Pageview on visibility-hidden (mobile-reliable)
        fireEngagedPageview();
      } else {
        lastTickMs = performance.now();
        isPageActive = true;
        startHeartbeat();
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Scroll milestones
  // ---------------------------------------------------------------------------
  function checkScroll() {
    try {
      var scrollY = window.scrollY || window.pageYOffset || 0;
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      if (docHeight <= 0) return;
      var pct = Math.min(100, Math.round(((scrollY + viewport) / docHeight) * 100));
      if (pct > maxScrollPct) maxScrollPct = pct;

      for (var i = 0; i < SCROLL_MILESTONES.length; i++) {
        var m = SCROLL_MILESTONES[i];
        if (pct >= m && !milestonesFired[m]) {
          milestonesFired[m] = true;
          window.safeCapture('Scroll Milestone', {
            milestone_pct: m,
            pathname: pathnameAtLoad,
            seconds_to_milestone: getCurrentDwellSeconds()
          });
        }
      }
    } catch (e) {}
  }

  // Throttle via rAF to avoid scroll-handler thrash
  var scrollScheduled = false;
  function onScroll() {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(function () {
      scrollScheduled = false;
      checkScroll();
    });
  }

  // ---------------------------------------------------------------------------
  // Engaged Pageview — synthetic event fired on leave
  // Idempotent: only fires once per pageview, even if multiple unload signals fire.
  // ---------------------------------------------------------------------------
  function fireEngagedPageview() {
    try {
      if (engagedPageviewFired) return;
      engagedPageviewFired = true;
      accrueActiveTime();
      var dwellSec = Math.round(activeMs / 1000);
      var isEngaged = dwellSec >= 30 && maxScrollPct >= 50;
      var pageviewId = null;
      try {
        if (window.posthog && typeof window.posthog.get_property === 'function') {
          pageviewId = window.posthog.get_property('$pageview_id') || null;
        }
      } catch (e) {}

      window.safeCapture('Engaged Pageview', {
        pathname: pathnameAtLoad,
        pageview_id: pageviewId,
        dwell_seconds: dwellSec,
        max_scroll_pct: maxScrollPct,
        is_engaged: isEngaged
      });
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Wire it up
  // ---------------------------------------------------------------------------
  try {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('beforeunload', fireEngagedPageview);
    window.addEventListener('pagehide', fireEngagedPageview); // mobile-reliable
    if (isPageActive) startHeartbeat();
    // Initial scroll check (in case user lands mid-page on long PDP)
    checkScroll();
  } catch (e) {
    if (window.console && console.warn) {
      console.warn('[posthog-engagement] boot failed:', e);
    }
  }
})();
